import type { ClientFields, DocCategorie, DocTip } from '../api';

/** Validare CNP (aceeași regulă ca pe server). */
export function checkCnp(raw: string): { valid: boolean; birthDate?: string; reason?: string } {
  const cnp = raw.replace(/\s/g, '');
  if (!/^\d{13}$/.test(cnp)) return { valid: false, reason: 'CNP-ul are 13 cifre' };
  const key = '279146358279';
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += Number(cnp[i]) * Number(key[i]);
  const c = sum % 11 === 10 ? 1 : sum % 11;
  if (c !== Number(cnp[12]) || cnp[0] === '0') return { valid: false, reason: 'Cifra de control nu corespunde' };
  const s = Number(cnp[0]);
  const yy = Number(cnp.slice(1, 3));
  const century = s <= 2 ? 1900 : s <= 4 ? 1800 : s <= 6 ? 2000 : yy <= new Date().getFullYear() % 100 ? 2000 : 1900;
  const d = new Date(Date.UTC(century + yy, Number(cnp.slice(3, 5)) - 1, Number(cnp.slice(5, 7))));
  if (d.getUTCDate() !== Number(cnp.slice(5, 7))) return { valid: false, reason: 'Data nașterii din CNP nu este validă' };
  return { valid: true, birthDate: d.toISOString().slice(0, 10) };
}

export const TIP_LABEL: Record<DocTip, string> = {
  CN: 'Certificat de naștere', CI: 'Carte de identitate', CEI: 'Carte electronică de identitate',
  CAD: 'Certificat de atestare a domiciliului', PC: 'Permis de conducere', PAS: 'Pașaport', AD: 'Alt document',
};
/** Categoriile, în ordinea din fișă */
export const CATEGORII: { cod: DocCategorie; label: string; icon: string }[] = [
  { cod: 'CN', label: 'Certificat de naștere', icon: '👶' },
  { cod: 'CI', label: 'Carte de identitate', icon: '🪪' },
  { cod: 'CAD', label: 'Certificat de atestare domiciliu', icon: '🏠' },
  { cod: 'PC', label: 'Permis de conducere', icon: '🚗' },
  { cod: 'PAS', label: 'Pașaport', icon: '🛂' },
  { cod: 'AD', label: 'Alte documente', icon: '📄' },
];

export const TIPURI_STRADA = ['Aleea', 'Bulevardul', 'Calea', 'Cartier', 'Colonia', 'Cvartalul', 'Drumul', 'Fundătura',
  'Intrarea', 'Piața', 'Prelungirea', 'Splaiul', 'Strada', 'Șirul', 'Șoseaua', 'Ulița'];
const ABBR: Record<string, string> = {
  Aleea: 'Al.', Bulevardul: 'Bd.', Calea: 'Calea', Cartier: 'Cart.', Colonia: 'Col.', Cvartalul: 'Cv.', Drumul: 'Drum.',
  Fundătura: 'Fdt.', Intrarea: 'Intr.', Piața: 'Piața', Prelungirea: 'Prel.', Splaiul: 'Spl.', Strada: 'Str.', Șirul: 'Șir.',
  Șoseaua: 'Șos.', Ulița: 'Ul.',
};

/** „Str. Noua nr. 44, Codlea, jud. Brașov, cod 505100” */
export function addressLine(c: Partial<ClientFields>): string {
  const parts: string[] = [];
  if (c.strada) parts.push(`${ABBR[c.tipStrada ?? ''] ?? 'Str.'} ${c.strada}`);
  const det = [c.numar && `nr. ${c.numar}`, c.bloc && `bl. ${c.bloc}`, c.scara && `sc. ${c.scara}`, c.etaj && `et. ${c.etaj}`, c.apartament && `ap. ${c.apartament}`]
    .filter(Boolean).join(', ');
  const first = [parts[0], det].filter(Boolean).join(' ');
  const out = [first, c.localitate, c.judet && !/Bucure[sș]ti/i.test(c.judet) ? `jud. ${c.judet}` : null, c.codPostal && `cod ${c.codPostal}`]
    .filter(Boolean);
  return out.join(', ');
}

/** Starea unui document după data expirării. */
export function expiryStatus(date: string | null): { label: string; cls: string } | null {
  if (!date) return null;
  const days = Math.floor((new Date(date + 'T00:00:00').getTime() - new Date(new Date().toDateString()).getTime()) / 86400000);
  if (days < 0) return { label: `expirat de ${-days} zile`, cls: 'tag danger' };
  if (days === 0) return { label: 'expiră azi', cls: 'tag danger' };
  if (days <= 60) return { label: `expiră în ${days} zile`, cls: 'tag warn' };
  return { label: 'valabil', cls: 'tag ok' };
}

export const fmtDate = (d: string | null | undefined) => (d ? d.slice(0, 10).split('-').reverse().join('.') : '—');

/**
 * Micșorează pozele mari înainte de trimitere (serviciul de citire acceptă max. 4 MB pe nivelul gratuit).
 * PDF-urile și pozele mici rămân neschimbate.
 */
export async function prepareUpload(file: File): Promise<{ blob: Blob; name: string }> {
  if (!file.type.startsWith('image/') || /heic|heif/i.test(file.type)) return { blob: file, name: file.name };
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return { blob: file, name: file.name };
  const max = 2600;
  const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
  if (scale === 1 && file.size < 3.5 * 1024 * 1024) return { blob: file, name: file.name };
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.85));
  return blob ? { blob, name: file.name.replace(/\.[^.]+$/, '') + '.jpg' } : { blob: file, name: file.name };
}

/** Lipește două poze (față + verso) una sub alta, ca un singur document pentru citire și păstrare. */
export async function stitchImages(files: File[]): Promise<File> {
  const bitmaps = await Promise.all(files.map((f) => createImageBitmap(f)));
  const width = Math.min(2000, Math.max(...bitmaps.map((b) => b.width)));
  const heights = bitmaps.map((b) => Math.round(b.height * (width / b.width)));
  const gap = 24;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = heights.reduce((a, b) => a + b, 0) + gap * (bitmaps.length - 1);
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  let y = 0;
  bitmaps.forEach((b, i) => { ctx.drawImage(b, 0, y, width, heights[i]); y += heights[i] + gap; });
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.85));
  if (!blob) throw new Error('Pozele nu au putut fi combinate.');
  return new File([blob], files[0].name.replace(/\.[^.]+$/, '') + '-fata-verso.jpg', { type: 'image/jpeg' });
}
